import { ThunkAction } from 'redux-thunk';
import { Action } from 'redux';
import { AxiosResponse } from 'axios';

import { RootState } from 'reducers';
import { Progress } from 'actions/actionUtils';

import { CustomComponent, PosPlugin } from '../../plugin';
import {
  Country,
  EFSTAConf,
  EFSTAConfCompany,
  EFSTAConfPos,
  EFSTAConfUser,
  EFSTAConfWarehouse,
} from '../types/pluginTypes';

import { ATFiscalIntegration } from './AT/AT';
import { DEFiscalIntegration } from './DE/DE';

export type OnChange = (
  key: keyof EFSTAConf | keyof EFSTAConfCompany,
) => (event: React.ChangeEvent<HTMLInputElement>) => void;

type Props = Parameters<
  Required<
    PosPlugin<
      EFSTAConf,
      EFSTAConfCompany,
      EFSTAConfWarehouse,
      EFSTAConfPos,
      EFSTAConfUser
    >
  >['ComponentConfiguration']
>[0] & {
  onChange: OnChange;
  currentConfig: EFSTAConf & EFSTAConfCompany;
};

export type EfstaConfiguration = CustomComponent<Props>;

type OnSaveSalesDocumentAp = Parameters<
  Required<Required<PosPlugin>['onSaveSalesDocument']>['on']
>[1];
type OnPrintReceiptP = Parameters<
  NonNullable<Required<PosPlugin>['onPrintReceipt']['on']>
>[0];
type OnCashInOutAp = Parameters<
  Required<Required<PosPlugin>['onTryCashInOut']>['on']
>[1];

/**
 * An object containing all the logic for a given fiscal integration.
 * A separate FiscalIntegration is defined for each country,
 * and the plugin will use methods and properties from whichever one is selected in configuration
 *
 * By analogy, each FiscalIntegration is like a POSPlugin, with the rest of the EFSTA plugin being like core POS code
 */
export interface FiscalIntegration {
  onOpenPaymentModal: Required<Required<PosPlugin>['onOpenPaymentModal']>['on'];

  onSaveDocument(
    requests: OnSaveSalesDocumentAp,
  ): ThunkAction<
    Promise<OnSaveSalesDocumentAp>,
    RootState,
    Progress | undefined,
    Action
  >;

  onCashInOut?(
    sum: OnCashInOutAp['sum'],
  ): ThunkAction<Promise<AxiosResponse<unknown>>, RootState, unknown, Action>;

  onCloseDay?(): ThunkAction<
    Promise<AxiosResponse<unknown>>,
    RootState,
    unknown,
    Action
  >;

  afterSaveDocument?(
    requests: OnSaveSalesDocumentAp,
  ): ThunkAction<any, RootState, unknown, Action>;

  ComponentConfiguration: EfstaConfiguration;
  ComponentHeader: PosPlugin['ComponentHeader'];
  UICustomTableBillRow?: PosPlugin['UICustomTableBillRow'];
  getStatus: PosPlugin['getStatus'];
  components?: PosPlugin['components'];

  onStartNewSale?(): ThunkAction<Promise<unknown>, RootState, unknown, Action>;

  onReprintReceipt?(
    params: OnPrintReceiptP,
  ): ThunkAction<Promise<void>, RootState, unknown, Action>;

  getIsConfigValid?: (config: any) => boolean;
}

/**
 * Fallback integration to use if theres a configuration error (f.ex. no country selected)
 * This should just throw errors everywhere to alert the user that something is wrong and prevent sales from being made
 */
export const NULLFiscalIntegration: FiscalIntegration = {
  ComponentConfiguration: () => null,
  ComponentHeader: () => null,
  onCloseDay: () => async () => {
    throw new Error('No fiscal integration selected');
  },
  onCashInOut: () => async () => {
    throw new Error('No fiscal integration selected');
  },
  onSaveDocument: () => async () => {
    throw new Error('No fiscal integration selected');
  },
  onOpenPaymentModal: () => async () => {
    throw new Error('No fiscal integration selected');
  },
  getStatus: () => ({
    type: 'error',
    message: 'No country selected',
  }),
};

export const fiscalIntegrations: Record<'NULL' | Country, FiscalIntegration> = {
  DE: DEFiscalIntegration,
  AT: ATFiscalIntegration,
  NULL: NULLFiscalIntegration,
};
